/**
 * Shared Access Tab Component
 * 
 * Displays the list of shared accesses (secondary users) for the current user.
 * Allows adding, editing, blocking/unblocking, and deleting shared accesses.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSharedAccessStore, useAccountStore, useWorkspaceStore } from '../../store';
import { Card, Button, Modal } from '../ui';
import SharedAccessForm from './SharedAccessForm';
import type { SharedAccess } from '@budgira/shared';

const SharedAccessTab = () => {
  const { t } = useTranslation();
  const {
    sharedAccesses,
    isLoading,
    error,
    fetchSharedAccesses,
    blockSharedAccess,
    unblockSharedAccess,
    deleteSharedAccess,
    sendInvitation,
    clearError,
  } = useSharedAccessStore();
  
  const { accounts, fetchAccounts } = useAccountStore();
  const { currentWorkspace } = useWorkspaceStore();
  
  // Filter shared accesses by current workspace
  const workspaceSharedAccesses = currentWorkspace
    ? sharedAccesses.filter(sa => sa.workspaceId === currentWorkspace.id)
    : [];
  
  // Modal states
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingAccess, setEditingAccess] = useState<SharedAccess | null>(null);
  const [deleteConfirmAccess, setDeleteConfirmAccess] = useState<SharedAccess | null>(null);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [invitationMessage, setInvitationMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // Load data on mount
  useEffect(() => {
    fetchSharedAccesses();
    fetchAccounts();
  }, [fetchSharedAccesses, fetchAccounts]);
  
  // Clear invitation message after 5 seconds
  useEffect(() => {
    if (invitationMessage) {
      const timer = setTimeout(() => setInvitationMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [invitationMessage]);
  
  // Handlers
  const handleAddClick = () => {
    setEditingAccess(null);
    setIsFormOpen(true);
    clearError();
  };
  
  const handleEditClick = (access: SharedAccess) => {
    setEditingAccess(access);
    setIsFormOpen(true);
    clearError();
  };
  
  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingAccess(null);
  };
  
  const handleFormSuccess = () => {
    setIsFormOpen(false);
    setEditingAccess(null);
    fetchSharedAccesses();
  };
  
  const handleBlockToggle = async (access: SharedAccess) => {
    setActionInProgress(access.id);
    try {
      if (access.status === 'blocked') {
        await unblockSharedAccess(access.id);
      } else {
        await blockSharedAccess(access.id);
      }
    } finally {
      setActionInProgress(null);
    }
  };
  
  const handleDeleteClick = (access: SharedAccess) => {
    setDeleteConfirmAccess(access);
  };
  
  const handleDeleteConfirm = async () => {
    if (!deleteConfirmAccess) return;
    setActionInProgress(deleteConfirmAccess.id);
    try {
      await deleteSharedAccess(deleteConfirmAccess.id);
      setDeleteConfirmAccess(null);
    } finally {
      setActionInProgress(null);
    }
  };
  
  const handleResendInvitation = async (access: SharedAccess) => {
    setActionInProgress(access.id);
    setInvitationMessage(null);
    try {
      const result = await sendInvitation(access.id);
      setInvitationMessage({
        type: result.success ? 'success' : 'error',
        text: result.success
          ? t('settings.sharedAccess.invitationSent')
          : result.message,
      });
    } finally {
      setActionInProgress(null);
    }
  };
  
  // Get account name by ID
  const getAccountName = (accountId: string): string => {
    const account = accounts.find(a => a.id === accountId);
    return account ? `${account.name} (${account.bankName})` : accountId;
  };
  
  // Status badge styling
  const getStatusBadge = (status: SharedAccess['status']) => {
    const styles: Record<SharedAccess['status'], string> = {
      pending: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300',
      active: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300',
      blocked: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300',
      user_deleted: 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400',
    };
    const labels: Record<SharedAccess['status'], string> = {
      pending: t('settings.sharedAccess.status.pending'),
      active: t('settings.sharedAccess.status.active'),
      blocked: t('settings.sharedAccess.status.blocked'),
      user_deleted: t('settings.sharedAccess.status.userDeleted'),
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };
  
  // Permission badge
  const getPermissionBadge = (permission: 'none' | 'read' | 'write') => {
    const styles = {
      none: 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400',
      read: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300',
      write: 'bg-violet-100 dark:bg-violet-900/40 text-violet-800 dark:text-violet-300',
    };
    const labels = {
      none: t('settings.sharedAccess.permission.none'),
      read: t('settings.sharedAccess.permission.read'),
      write: t('settings.sharedAccess.permission.write'),
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles[permission]}`}>
        {labels[permission]}
      </span>
    );
  };
  
  // Render empty state
  const renderEmptyState = () => (
    <Card>
      <div className="text-center py-12">
        <svg
          className="mx-auto h-12 w-12 text-slate-400 dark:text-slate-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
        <h3 className="mt-4 text-lg font-medium text-slate-900 dark:text-slate-100">
          {t('settings.sharedAccess.empty.title')}
        </h3>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 max-w-sm mx-auto">
          {t('settings.sharedAccess.empty.description')}
        </p>
        <div className="mt-6">
          <Button onClick={handleAddClick}>
            {t('settings.sharedAccess.add')}
          </Button>
        </div>
      </div>
    </Card>
  );
  
  // Render access card
  const renderAccessCard = (access: SharedAccess) => {
    const isActionInProgress = actionInProgress === access.id;
    const accountPermissions = access.accountPermissions.filter(ap => ap.permission !== 'none');
    const isUserDeleted = access.status === 'user_deleted';
    
    return (
      <Card key={access.id} className={`relative ${isUserDeleted ? 'opacity-60' : ''}`}>
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className={`text-lg font-semibold ${isUserDeleted ? 'text-slate-500 dark:text-slate-400' : 'text-slate-900 dark:text-slate-100'}`}>
              {access.firstName} {access.lastName}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">{access.email}</p>
          </div>
          <div className="flex items-center gap-2">
            {getStatusBadge(access.status)}
          </div>
        </div>
        
        {/* User deleted notice */}
        {isUserDeleted && access.deletedAt && (
          <div className="mb-4 p-3 bg-slate-100 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {access.deletedByType === 'admin' 
                ? t('settings.sharedAccess.deletedByAdminNotice', {
                    date: new Date(access.deletedAt).toLocaleDateString()
                  })
                : t('settings.sharedAccess.userDeletedNotice', {
                    date: new Date(access.deletedAt).toLocaleDateString()
                  })
              }
            </p>
          </div>
        )}
        
        {/* Permissions summary - only show if not deleted */}
        {!isUserDeleted && (
          <div className="space-y-3 mb-4">
            {/* Feature permissions */}
            {access.featurePermissions && (
              <div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
                  {t('settings.sharedAccess.featurePermissions')}:
                </p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-700 px-3 py-1.5 rounded">
                    <span className="text-slate-600 dark:text-slate-300">{t('settings.sharedAccess.features.categories')}</span>
                    {getPermissionBadge(access.featurePermissions.categories)}
                  </div>
                  <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-700 px-3 py-1.5 rounded">
                    <span className="text-slate-600 dark:text-slate-300">{t('settings.sharedAccess.features.paymentMethods')}</span>
                    {getPermissionBadge(access.featurePermissions.paymentMethods)}
                  </div>
                  <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-700 px-3 py-1.5 rounded">
                    <span className="text-slate-600 dark:text-slate-300">{t('settings.sharedAccess.features.checkbooks')}</span>
                    {getPermissionBadge(access.featurePermissions.checkbooks)}
                  </div>
                  <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-700 px-3 py-1.5 rounded">
                    <span className="text-slate-600 dark:text-slate-300">{t('settings.sharedAccess.features.projects')}</span>
                    {getPermissionBadge(access.featurePermissions.projects)}
                  </div>
                  <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-700 px-3 py-1.5 rounded">
                    <span className="text-slate-600 dark:text-slate-300">{t('settings.sharedAccess.features.reconciliation')}</span>
                    {getPermissionBadge(access.featurePermissions.reconciliation)}
                  </div>
                  <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-700 px-3 py-1.5 rounded">
                    <span className="text-slate-600 dark:text-slate-300">{t('settings.sharedAccess.features.statements')}</span>
                    {getPermissionBadge(access.featurePermissions.statements)}
                  </div>
                  <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-700 px-3 py-1.5 rounded">
                    <span className="text-slate-600 dark:text-slate-300">{t('settings.sharedAccess.features.budgets')}</span>
                    {getPermissionBadge(access.featurePermissions.budgets)}
                  </div>
                  <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-700 px-3 py-1.5 rounded">
                    <span className="text-slate-600 dark:text-slate-300">{t('settings.sharedAccess.features.apiKeys')}</span>
                    {getPermissionBadge(access.featurePermissions.apiKeys)}
                  </div>
                  <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-700 px-3 py-1.5 rounded">
                    <span className="text-slate-600 dark:text-slate-300">{t('settings.sharedAccess.features.alerts')}</span>
                    {getPermissionBadge(access.featurePermissions.alerts)}
                  </div>
                  <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-700 px-3 py-1.5 rounded">
                    <span className="text-slate-600 dark:text-slate-300">{t('settings.sharedAccess.features.counterparties')}</span>
                    {getPermissionBadge(access.featurePermissions.counterparties)}
                  </div>
                </div>
              </div>
            )}
            
            {/* Legacy permissions display (fallback if featurePermissions not available) */}
            {!access.featurePermissions && (
              <div className="flex flex-wrap gap-2">
                <span className="text-sm text-slate-600 dark:text-slate-300">
                  {t('settings.sharedAccess.canManageCategories')}:
                  <span className={`ml-1 font-medium ${access.canManageCategories ? 'text-green-600 dark:text-green-400' : 'text-slate-400 dark:text-slate-500'}`}>
                    {access.canManageCategories ? t('common.yes') : t('common.no')}
                  </span>
                </span>
                <span className="text-slate-300 dark:text-slate-600">|</span>
                <span className="text-sm text-slate-600 dark:text-slate-300">
                  {t('settings.sharedAccess.canManagePaymentMethods')}:
                  <span className={`ml-1 font-medium ${access.canManagePaymentMethods ? 'text-green-600 dark:text-green-400' : 'text-slate-400 dark:text-slate-500'}`}>
                    {access.canManagePaymentMethods ? t('common.yes') : t('common.no')}
                  </span>
                </span>
              </div>
            )}
            
            {/* Account permissions */}
            {accountPermissions.length > 0 && (
              <div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
                  {t('settings.sharedAccess.accountPermissions')}:
                </p>
                <div className="space-y-1">
                  {accountPermissions.map(ap => (
                    <div key={ap.accountId} className="flex items-center justify-between text-sm bg-slate-50 dark:bg-slate-700 px-3 py-1.5 rounded">
                      <span className="text-slate-700 dark:text-slate-200">{getAccountName(ap.accountId)}</span>
                      {getPermissionBadge(ap.permission)}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {accountPermissions.length === 0 && (
              <p className="text-sm text-slate-400 dark:text-slate-500 italic">
                {t('settings.sharedAccess.noAccountPermissions')}
              </p>
            )}
          </div>
        )}
        
        {/* Dates */}
        <div className="text-xs text-slate-400 dark:text-slate-500 mb-4">
          {access.lastLoginAt && !isUserDeleted && (
            <span>
              {t('settings.sharedAccess.lastLogin')}: {new Date(access.lastLoginAt).toLocaleDateString()}
            </span>
          )}
          {!access.lastLoginAt && access.status === 'pending' && (
            <span>{t('settings.sharedAccess.neverConnected')}</span>
          )}
        </div>
        
        {/* Actions */}
        <div className="flex flex-wrap items-center gap-2 pt-4 border-t border-slate-100 dark:border-slate-700">
          {/* For user_deleted: only show "Remove from list" button */}
          {isUserDeleted ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleDeleteClick(access)}
              disabled={isActionInProgress}
            >
              {t('settings.sharedAccess.removeFromList')}
            </Button>
          ) : (
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleEditClick(access)}
                disabled={isActionInProgress}
              >
                {t('common.edit')}
              </Button>
              
              {access.status === 'pending' && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleResendInvitation(access)}
                  disabled={isActionInProgress}
                >
                  {isActionInProgress ? t('common.sending') : t('settings.sharedAccess.resendInvitation')}
                </Button>
              )}
              
              <Button
                variant={access.status === 'blocked' ? 'secondary' : 'danger'}
                size="sm"
                onClick={() => handleBlockToggle(access)}
                disabled={isActionInProgress || access.status === 'pending'}
              >
                {access.status === 'blocked'
                  ? t('settings.sharedAccess.unblock')
                  : t('settings.sharedAccess.block')}
              </Button>
              
              <Button
                variant="danger"
                size="sm"
                onClick={() => handleDeleteClick(access)}
                disabled={isActionInProgress}
              >
                {t('settings.sharedAccess.unlink')}
              </Button>
            </>
          )}
        </div>
      </Card>
    );
  };
  
  // Main render
  return (
    <div className="space-y-6">
      {/* Header with Add button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {t('settings.sharedAccess.title')}
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t('settings.sharedAccess.description')}
          </p>
        </div>
        {workspaceSharedAccesses.length > 0 && (
          <Button onClick={handleAddClick}>
            {t('settings.sharedAccess.add')}
          </Button>
        )}
      </div>
      
      {/* Error message - only show when modal is closed */}
      {error && !isFormOpen && (
        <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400">
          {error}
        </div>
      )}
      
      {/* Invitation message */}
      {invitationMessage && (
        <div
          className={`p-4 rounded-lg border ${
            invitationMessage.type === 'success'
              ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800 text-green-700 dark:text-green-400'
              : 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800 text-red-600 dark:text-red-400'
          }`}
        >
          {invitationMessage.text}
        </div>
      )}
      
      {/* Loading state */}
      {isLoading && workspaceSharedAccesses.length === 0 && (
        <div className="space-y-4">
          {[1, 2].map(i => (
            <div key={i} className="h-48 bg-slate-100 dark:bg-slate-700 rounded-xl animate-pulse" />
          ))}
        </div>
      )}
      
      {/* Empty state or list */}
      {!isLoading && workspaceSharedAccesses.length === 0 && renderEmptyState()}
      
      {workspaceSharedAccesses.length > 0 && (
        <div className="space-y-4">
          {workspaceSharedAccesses.map(renderAccessCard)}
        </div>
      )}
      
      {/* Add/Edit Form Modal */}
      <SharedAccessForm
        isOpen={isFormOpen}
        onClose={handleFormClose}
        onSuccess={handleFormSuccess}
        editingAccess={editingAccess}
        accounts={accounts}
      />
      
      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteConfirmAccess}
        onClose={() => setDeleteConfirmAccess(null)}
        title={t('settings.sharedAccess.deleteConfirm.title')}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setDeleteConfirmAccess(null)}
              disabled={!!actionInProgress}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteConfirm}
              disabled={!!actionInProgress}
            >
              {actionInProgress ? t('common.loading') : t('settings.sharedAccess.unlink')}
            </Button>
          </>
        }
      >
        <p className="text-slate-600 dark:text-slate-300">
          {t('settings.sharedAccess.deleteConfirm.message', {
            name: deleteConfirmAccess
              ? `${deleteConfirmAccess.firstName} ${deleteConfirmAccess.lastName}`
              : '',
          })}
        </p>
      </Modal>
    </div>
  );
};

export default SharedAccessTab;
